import { useEffect } from 'react';
import { getConfig, getFeatureStatus } from '../config';
import { isNative, isNativeVideoOrContent } from '../utils/ast';
import {
    AdObj,
    BannerAdObj,
    NativeAdObj,
    VideoAdObj,
} from '../../shared/types/ast';
import { UNLEASH_FEATURE_NAMES } from '../../shared/unleash/feature-names';

const CACHE_KEY = 'result-advertising-cache';

const heightCachingEnabled = getFeatureStatus(
    UNLEASH_FEATURE_NAMES.enableResultCaching,
);

export function useAdSizeCache({
    placementId,
    adObj,
    status,
}: {
    placementId: string;
    adObj: AdObj;
    status: string;
}) {
    useEffect(() => {
        const container = getContainer(placementId);
        if (container) {
            const cache = JSON.parse(sessionStorage.getItem(CACHE_KEY));
            if (cache && cache[placementId]) {
                if (heightCachingEnabled) {
                    container.style.minHeight = cache[placementId].height;
                }
                container.className = cache[placementId].className;
            }
        }
    }, [placementId]);

    useEffect(() => {
        if (status === 'loaded' && adObj?.adType) {
            const pageType = getConfig()?.pageType;

            // test that placement id = board_n or netboard_n where n is a number

            const isResultAd =
                pageType === 'result' && isResultBannerAd(placementId);
            if (!isResultAd) return;

            const isNativeAd = isNative(adObj);
            const isNativeContentOrVideoAd = Boolean(
                isNativeAd && isNativeVideoOrContent(adObj),
            );

            const isFullWidth = isNativeContentOrVideoAd || !isNativeAd;

            const container = getContainer(placementId);

            const _adObj = adObj as BannerAdObj | VideoAdObj | NativeAdObj;
            if (container) {
                setFullWidthAdContainer({
                    container,
                    height: container.offsetHeight || _adObj?.height || 0,
                    isFullWidth,
                });

                setCache(placementId, container);
            }
        }
    }, [adObj, placementId, status]);

    useEffect(() => {
        if (status === 'failed') {
            const container = getContainer(placementId);

            if (container) {
                setFullWidthAdContainer({
                    container,
                    height: 0,
                    isFullWidth: false,
                });
                setCache(placementId, container);
            }
        }
    }, [placementId, status]);
}

function getContainer(placementId) {
    const containerId = placementId + '--container';
    // Get root element from result list
    return document.getElementById(containerId);
}

function setFullWidthAdContainer({
    container,
    height,
    isFullWidth,
}: {
    container: HTMLElement;
    height: number;
    isFullWidth: boolean;
}) {
    if (isFullWidth) {
        container.className = 'col-span-full';
        if (heightCachingEnabled) {
            container.style.minHeight = height + 'px';
        }
    } else {
        // remove full width
        container.className = container.className?.replace('col-span-full', '');
        // remove container style height
        if (heightCachingEnabled) {
            container.style.minHeight = '';
        }
    }
}

function isResultBannerAd(placementId: string): boolean {
    const regex = /board_\d+|netboard_\d+/;
    return regex.test(placementId);
}

function setCache(placementId: string, container: HTMLElement) {
    const cache = JSON.parse(sessionStorage.getItem(CACHE_KEY)) || {};
    cache[placementId] = {
        height: container.style.height,
        className: container.className,
    };

    sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
}
